<template>
  <div
    class="flashcard-canvas-container flex-1 flex flex-col items-center justify-center p-2 sm:p-4 w-full my-auto cursor-pointer"
    @click="handleCardClick"
  >
    <!-- Active Card Presentation -->
    <div
      v-if="card"
      class="flashcard-card w-full max-w-lg min-h-[280px] rounded-2xl border p-6 sm:p-8 flex flex-col items-center justify-center text-center shadow-xl transition-all select-none"
    >
      <!-- Image attachment if present -->
      <div v-if="card.imageUrl" class="card-image-wrapper mb-4 w-full overflow-hidden rounded-xl border border-black/5 dark:border-white/5">
        <img :src="card.imageUrl" alt="Card Illustration" class="w-full max-h-56 object-cover rounded-xl" />
      </div>

      <!-- Informational / Menu View -->
      <div
        v-if="card.isInformational || (!card.translation && !card.transcription && !card.isTypingDrill && card.word.length > 30)"
        class="info-content text-center py-4 w-full"
      >
        <div class="text-4xl mb-3 select-none">🦉</div>
        <div class="text-base font-medium whitespace-pre-wrap leading-relaxed text-[var(--ui-text)]">
          {{ card.rawText || card.word }}
        </div>
      </div>

      <!-- Translation / Drill Result View (After Check is pressed or from server) -->
      <div v-else-if="card.isTranslationResult || isTypingSubmitted" class="result-content w-full flex flex-col items-center justify-center text-center py-3">
        <!-- Status indicator: only useful info -->
        <div class="result-status-badge mb-2 flex items-center justify-center gap-1.5 text-sm font-medium">
          <i
            class="text-base"
            :class="isResultCorrect ? 'fa-solid fa-circle-check text-[var(--ui-success)]' : 'fa-solid fa-circle-xmark text-[var(--ui-danger)]'"
          ></i>
          <span :class="isResultCorrect ? 'text-[var(--ui-success-text)]' : 'text-[var(--ui-danger-text)]'">
            {{ isResultCorrect ? 'Translation is Correct!' : 'Wrong translation' }}
          </span>
        </div>

        <!-- Target Word: Centered, clean, harmonious typography, NO ugly audio button -->
        <h2 class="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--ui-text)] word-text my-1.5">
          {{ resultWord }}
        </h2>

        <!-- Phonetic transcription in clean, matching UI font -->
        <p v-if="resultTranscription" class="transcription-text text-sm text-[var(--ui-text-muted)] font-normal tracking-wide mt-0.5">
          [{{ resultTranscription }}]
        </p>

        <!-- Translation / Meaning in harmonious size and font -->
        <p v-if="resultTranslation && resultTranslation !== resultWord" class="translation-text text-base sm:text-lg text-[var(--ui-text)] font-normal mt-2.5">
          {{ resultTranslation }}
        </p>

        <!-- If incorrect: Show what user typed to allow direct comparison -->
        <div v-if="!isResultCorrect && (card.submittedInput || submittedInput)" class="user-attempt-box mt-3 text-sm flex items-center justify-center gap-1.5 font-normal text-[var(--ui-text-muted)]">
          <span>Your answer:</span>
          <span class="font-medium text-[var(--ui-danger-text)] line-through">{{ card.submittedInput || submittedInput }}</span>
        </div>

        <!-- Minimalist bottom hint (proportional font size, white/surface card background, clean text) -->
        <p class="result-tap-hint mt-6 text-xs font-normal text-[var(--ui-text-muted)]/75 select-none tracking-wide">
          Tap to continue
        </p>
      </div>

      <!-- Level 5: Writing Drill Mode -->
      <div v-else-if="card.isTypingDrill || card.level === 5" class="w-full flex flex-col items-center justify-center my-auto">
        <!-- Prompt (Translation / Meaning) -->
        <div class="my-3 text-center">
          <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ui-text)]">
            {{ card.translation || card.word }}
          </h2>
        </div>

        <!-- Typing Drill Input & Action -->
        <div class="w-full max-w-sm flex items-center gap-2 my-4">
          <input
            v-model="userTranslationInput"
            type="text"
            placeholder="Type translation here..."
            class="ui-input flex-1 text-center font-medium"
            @keydown.enter.prevent="submitTyping"
          />
          <UiButton
            variant="primary"
            @click.stop="submitTyping"
          >
            {{ userTranslationInput.trim() ? 'Check' : 'Skip' }}
          </UiButton>
        </div>
      </div>

      <!-- Level 4: Reverse Card Mode -->
      <div v-else-if="card.isReverse || card.level === 4" class="w-full flex flex-col items-center justify-center my-auto">
        <!-- Prompt (Translation visible) -->
        <div class="my-2 text-center">
          <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ui-text)]">
            {{ card.translation }}
          </h2>
        </div>

        <!-- Target word behind spoiler -->
        <div class="mt-6 sm:mt-8 mb-2 w-full flex flex-col items-center justify-center">
          <div v-if="isRevealed" class="flex flex-col items-center gap-1.5">
            <div class="flex items-center gap-2">
              <span class="text-3xl sm:text-4xl font-extrabold tracking-tight word-text">{{ card.word }}</span>
            </div>
            <p v-if="card.transcription" class="text-sm text-[var(--ui-text-muted)] font-normal tracking-wide">
              [{{ card.transcription }}]
            </p>
          </div>
          <UiButton
            v-else
            variant="secondary"
            shape="pill"
            class="reveal-button px-5 py-2.5 text-sm font-semibold transition active:scale-95"
          >
            <i class="fa-solid fa-eye mr-2"></i>
            <span>Tap to reveal target word</span>
          </UiButton>
        </div>
      </div>

      <!-- Standard Levels 1-3 View -->
      <template v-else>
        <!-- Main word -->
        <div class="word-header flex items-center justify-center gap-3 w-full my-2">
          <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight word-text">
            {{ card.word }}
          </h2>
        </div>

        <!-- Translation & Transcription (positioned above action items; transcription hidden with translation behind spoiler) -->
        <div class="translation-section mt-5 sm:mt-6 mb-2 w-full flex flex-col items-center justify-center gap-1.5">
          <UiButton
            v-if="card.isSpoiler && !isRevealed"
            variant="secondary"
            shape="pill"
            class="reveal-button px-5 py-2.5 text-sm font-semibold transition active:scale-95"
          >
            <i class="fa-solid fa-eye mr-2"></i>
            <span>Tap to reveal translation</span>
          </UiButton>

          <template v-else>
            <p
              v-if="card.translation"
              class="translation-text text-2xl sm:text-3xl font-semibold text-[var(--ui-text)]"
            >
              {{ card.translation }}
            </p>
            <p
              v-if="card.transcription"
              class="transcription-text text-sm text-[var(--ui-text-muted)] font-normal tracking-wide"
            >
              [{{ card.transcription }}]
            </p>
          </template>
        </div>
      </template>

      <!-- Word Actions Toolbar (opened via down-to-up swipe gesture anywhere on the page) -->
      <div
        v-if="!card.isInformational && !card.isTranslationResult && !isTypingSubmitted && isToolbarRendered"
        class="word-actions-toolbar flex flex-wrap items-center justify-center gap-2 px-2 rounded-2xl bg-[var(--ui-surface-subtle)] shadow-xs"
        :class="{ 'word-actions-toolbar--pulse': isHalfwayPulse }"
        :style="toolbarStyle"
        :data-gesture-committed="gestureCommitted ? 'true' : 'false'"
        @click.stop
      >
        <UiButton
          variant="ghost"
          size="md"
          shape="pill"
          class="min-h-10 px-3.5 py-2 text-sm font-medium text-[var(--ui-text)] hover:text-blue-500"
          title="Promote star level"
          @click="handleReaction('star_up')"
        >
          <i class="fa-solid fa-thumbs-up mr-1.5 text-base text-blue-500"></i>
          <span>+1 Star</span>
        </UiButton>

        <UiButton
          variant="ghost"
          size="md"
          shape="pill"
          class="min-h-10 px-3.5 py-2 text-sm font-medium text-[var(--ui-text)] hover:text-slate-400"
          title="Demote star level"
          @click="handleReaction('star_down')"
        >
          <i class="fa-solid fa-thumbs-down mr-1.5 text-base text-slate-400"></i>
          <span>-1 Star</span>
        </UiButton>

        <UiButton
          variant="ghost"
          size="md"
          shape="pill"
          class="min-h-10 px-3.5 py-2 text-sm font-medium text-[var(--ui-text)] hover:text-amber-500"
          title="Postpone word"
          @click="handleReaction('postpone')"
        >
          <i class="fa-solid fa-clock mr-1.5 text-base text-amber-500"></i>
          <span>Postpone</span>
        </UiButton>

        <UiButton
          variant="ghost"
          size="md"
          shape="pill"
          class="min-h-10 px-3.5 py-2 text-sm font-medium text-[var(--ui-text)] hover:text-rose-500"
          title="Mark as learned"
          @click="handleReaction('learned')"
        >
          <i class="fa-solid fa-heart mr-1.5 text-base text-rose-500"></i>
          <span>Learned</span>
        </UiButton>

        <UiButton
          variant="ghost"
          size="md"
          shape="pill"
          class="min-h-10 px-3.5 py-2 text-sm font-medium text-[var(--ui-text)] hover:text-gray-400"
          title="Remove word"
          @click="handleReaction('trash')"
        >
          <i class="fa-solid fa-trash mr-1.5 text-base text-gray-400"></i>
          <span>Remove</span>
        </UiButton>
      </div>

      <!-- Examples / Hints (Standard Levels 1-3) -->
      <div
        v-if="!card.isInformational && !card.isTranslationResult && !isTypingSubmitted && !card.isTypingDrill && card.level !== 5 && !card.isReverse && card.level !== 4 && card.examples && card.examples.length > 0"
        class="examples-section w-full mt-4 text-left border-t border-[var(--ui-border)] pt-3"
      >
        <h4 class="text-xs uppercase font-bold text-[var(--ui-text-muted)] mb-2">Examples / Hints:</h4>
        <div
          v-for="(ex, idx) in card.examples"
          :key="idx"
          class="example-item text-sm my-1.5 p-2.5 rounded-xl border border-[var(--ui-border)] bg-[var(--ui-surface-muted)]"
        >
          <p class="font-medium text-[var(--ui-text)]">{{ ex.original }}</p>
          <p v-if="ex.translation" class="text-xs text-[var(--ui-text-muted)] mt-0.5">{{ ex.translation }}</p>
        </div>
      </div>
    </div>

    <!-- Empty / Welcome state matching TaskManager surfaces -->
    <div
      v-else
      class="welcome-card w-full max-w-lg min-h-[280px] rounded-2xl border p-8 flex flex-col items-center justify-center text-center shadow-md backdrop-blur-md"
    >
      <div class="text-5xl mb-4 select-none">🦉</div>
      <h3 class="text-2xl font-bold mb-2 text-[var(--ui-text)]">Welcome to Geobu</h3>
      <p class="text-sm text-[var(--ui-text-muted)] mb-6">
        Practice words and expand your vocabulary with spaced repetition.
      </p>
      <p class="text-xs text-[var(--ui-text-subtle)]">
        Tap the action buttons below to start learning.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { UiButton } from '../ui';
import type { CardData } from '../types';
import { api } from '../services/api';
import { learningEngine, checkTranslationMatch } from '../services/LearningWorkflowEngine';
import { localSync } from '../services/LocalSyncService';

const props = defineProps<{
  card: CardData | null;
}>();

const emit = defineEmits<{
  (e: 'submit-reply', text: string): void;
  (e: 'word-action', action: string, card: CardData): void;
  (e: 'next-word'): void;
}>();

const FULL_SWIPE_PX = 80;
const HALFWAY_CLICK_PX = 45;
const DRAG_SLOP_PX = 8;

const isRevealed = ref(false);
const showActions = ref(false);

// Swipe-to-open / swipe-to-close gesture state
const menuProgress = ref(0);
const isDraggingMenu = ref(false);
const isClosingAnimation = ref(false);
const gestureCommitted = ref(false);
const halfwayClicked = ref(false);
const isHalfwayPulse = ref(false);

let closeAnimTimer: ReturnType<typeof setTimeout> | null = null;
let pulseTimer: ReturnType<typeof setTimeout> | null = null;
let lastSwipeEndAt = 0;

let gestureActive = false;
let gestureStartX = 0;
let gestureStartY = 0;
let gestureStartOpen = false;
let activeTouchTracking = false;

const isToolbarRendered = computed(
  () => showActions.value || isDraggingMenu.value || isClosingAnimation.value
);

const toolbarStyle = computed(() => {
  const p = Math.min(1, Math.max(0, menuProgress.value));
  const translateY = (1 - p) * -14; // slides down into place when pulling down
  const scale = isHalfwayPulse.value ? 1.04 : 0.94 + 0.06 * p;
  const maxHeight = Math.round(p * 180);
  const padY = (8 * p).toFixed(1);
  const my = (12 * p).toFixed(1);

  return {
    opacity: String(Number(p.toFixed(3))),
    transform: `translateY(${translateY.toFixed(1)}px) scale(${scale.toFixed(3)})`,
    maxHeight: `${maxHeight}px`,
    paddingTop: `${padY}px`,
    paddingBottom: `${padY}px`,
    marginTop: `${my}px`,
    marginBottom: `${my}px`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: p > 0.02 ? 'var(--ui-border)' : 'transparent',
    overflow: p < 0.99 ? 'hidden' : 'visible',
    visibility: (p > 0.005 ? 'visible' : 'hidden') as 'visible' | 'hidden',
    boxShadow: p > 0.02 ? undefined : 'none',
    pointerEvents: (showActions.value && !isDraggingMenu.value && p > 0.9 ? 'auto' : 'none') as 'auto' | 'none',
    transition: isDraggingMenu.value
      ? 'none'
      : 'opacity 0.24s cubic-bezier(0.22, 1, 0.36, 1), transform 0.24s cubic-bezier(0.22, 1, 0.36, 1), max-height 0.24s cubic-bezier(0.22, 1, 0.36, 1), padding 0.24s cubic-bezier(0.22, 1, 0.36, 1), margin 0.24s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.24s ease',
  };
});

const triggerHalfwayClick = () => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(15);
    }
  } catch {
    // Ignore vibration errors on unsupported browsers
  }

  isHalfwayPulse.value = true;
  if (pulseTimer) clearTimeout(pulseTimer);
  pulseTimer = setTimeout(() => {
    isHalfwayPulse.value = false;
  }, 160);
};

const openActionsMenu = () => {
  if (closeAnimTimer) {
    clearTimeout(closeAnimTimer);
    closeAnimTimer = null;
  }
  isClosingAnimation.value = false;
  showActions.value = true;
  menuProgress.value = 1;
};

const closeActionsMenu = () => {
  if (!showActions.value && !isDraggingMenu.value && menuProgress.value === 0) {
    isClosingAnimation.value = false;
    return;
  }
  showActions.value = false;
  menuProgress.value = 0;
  gestureCommitted.value = false;
  halfwayClicked.value = false;
  isClosingAnimation.value = true;
  if (closeAnimTimer) clearTimeout(closeAnimTimer);
  closeAnimTimer = setTimeout(() => {
    isClosingAnimation.value = false;
    closeAnimTimer = null;
  }, 240);
};

const shouldIgnoreGestureTarget = (target: EventTarget | null): boolean => {
  if (!props.card || props.card.isInformational || props.card.isTranslationResult || isTypingSubmitted.value) {
    return true;
  }
  if (typeof document !== 'undefined' && document.querySelector('.ui-dialog')) {
    return true;
  }
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.closest('input, textarea, select, [role="slider"]')) {
    return true;
  }
  return false;
};

const beginGesture = (clientX: number, clientY: number, target: EventTarget | null) => {
  if (shouldIgnoreGestureTarget(target)) {
    gestureActive = false;
    return;
  }
  gestureActive = true;
  gestureStartX = clientX;
  gestureStartY = clientY;
  gestureStartOpen = showActions.value;
  isDraggingMenu.value = false;
  gestureCommitted.value = false;
  halfwayClicked.value = false;
};

const moveGesture = (clientX: number, clientY: number) => {
  if (!gestureActive) return;

  const dx = clientX - gestureStartX;
  const dy = gestureStartY - clientY; // positive = down-to-up (upward), negative = up-to-down (downward)
  const downDy = clientY - gestureStartY; // positive = downward (pull down)
  const upDy = dy; // positive = upward (push up)

  if (!isDraggingMenu.value) {
    // Cancel if predominantly horizontal movement
    if (Math.abs(dx) > Math.abs(dy) * 1.2 && Math.abs(dx) > 15) {
      gestureActive = false;
      return;
    }

    // Reversed: when closed, swipe DOWN (up to down) opens the menu
    if (!gestureStartOpen && downDy > DRAG_SLOP_PX) {
      if (closeAnimTimer) {
        clearTimeout(closeAnimTimer);
        closeAnimTimer = null;
      }
      isClosingAnimation.value = false;
      isDraggingMenu.value = true;
    } else if (gestureStartOpen && upDy > DRAG_SLOP_PX) {
      // Reversed: when open, swipe UP (down to up) closes the menu
      isDraggingMenu.value = true;
    } else {
      return;
    }
  }

  if (!gestureStartOpen) {
    // Downward swipe: opening action menu
    const clampedDownDy = Math.max(0, downDy);
    let progress: number;
    if (clampedDownDy <= HALFWAY_CLICK_PX) {
      progress = (clampedDownDy / HALFWAY_CLICK_PX) * 0.5;
    } else {
      progress =
        0.5 +
        Math.min(
          0.5,
          ((clampedDownDy - HALFWAY_CLICK_PX) / (FULL_SWIPE_PX - HALFWAY_CLICK_PX)) * 0.5
        );
    }
    menuProgress.value = Math.min(1, Math.max(0, progress));

    if (clampedDownDy >= HALFWAY_CLICK_PX) {
      gestureCommitted.value = true;
      if (!halfwayClicked.value) {
        halfwayClicked.value = true;
        triggerHalfwayClick();
      }
    } else {
      gestureCommitted.value = false;
      halfwayClicked.value = false;
    }
  } else {
    // Upward swipe: closing action menu
    const clampedUpDy = Math.max(0, upDy);
    let progress: number;
    if (clampedUpDy <= HALFWAY_CLICK_PX) {
      progress = 1.0 - (clampedUpDy / HALFWAY_CLICK_PX) * 0.5;
    } else {
      progress = Math.max(
        0,
        0.5 - ((clampedUpDy - HALFWAY_CLICK_PX) / (FULL_SWIPE_PX - HALFWAY_CLICK_PX)) * 0.5
      );
    }
    menuProgress.value = Math.min(1, Math.max(0, progress));

    if (clampedUpDy >= HALFWAY_CLICK_PX) {
      gestureCommitted.value = true;
      if (!halfwayClicked.value) {
        halfwayClicked.value = true;
        triggerHalfwayClick();
      }
    } else {
      gestureCommitted.value = false;
      halfwayClicked.value = false;
    }
  }
};

const endGesture = () => {
  if (!gestureActive) return;
  gestureActive = false;

  if (!isDraggingMenu.value) {
    return;
  }

  lastSwipeEndAt = Date.now();
  isDraggingMenu.value = false;

  if (!gestureStartOpen) {
    if (gestureCommitted.value) {
      openActionsMenu();
    } else {
      closeActionsMenu();
    }
  } else {
    if (gestureCommitted.value) {
      closeActionsMenu();
    } else {
      openActionsMenu();
    }
  }
};

const onWindowTouchStart = (e: TouchEvent) => {
  if (e.touches.length !== 1) return;
  activeTouchTracking = true;
  const t = e.touches[0];
  gestureStartX = t.clientX;
  gestureStartY = t.clientY;
  beginGesture(t.clientX, t.clientY, e.target);
};

const onWindowTouchMove = (e: TouchEvent) => {
  if (e.touches.length !== 1) return;
  const t = e.touches[0];
  const dx = t.clientX - gestureStartX;
  const dy = gestureStartY - t.clientY; // positive = down-to-up, negative = up-to-down

  // Block mobile browser pull-to-refresh & overscroll bounce on vertical gestures outside modals
  const targetEl = e.target as HTMLElement | null;
  const inDialog = Boolean(targetEl?.closest?.('.ui-dialog'));
  if (!inDialog && Math.abs(dy) >= Math.abs(dx)) {
    if (gestureActive || (dy < 0 && window.scrollY <= 0)) {
      if (e.cancelable) {
        e.preventDefault();
      }
    }
  }

  if (!gestureActive) return;
  moveGesture(t.clientX, t.clientY);
};

const onWindowTouchEnd = () => {
  endGesture();
  setTimeout(() => {
    activeTouchTracking = false;
  }, 50);
};

const onWindowPointerDown = (e: PointerEvent) => {
  if (activeTouchTracking || e.pointerType === 'touch') return;
  if (e.button !== 0) return;
  beginGesture(e.clientX, e.clientY, e.target);
};

const onWindowPointerMove = (e: PointerEvent) => {
  if (activeTouchTracking || e.pointerType === 'touch') return;
  moveGesture(e.clientX, e.clientY);
};

const onWindowPointerUp = (e: PointerEvent) => {
  if (activeTouchTracking || e.pointerType === 'touch') return;
  endGesture();
};

const onWindowClick = (e: MouseEvent) => {
  if (!showActions.value) return;
  if (Date.now() - lastSwipeEndAt < 250) return;
  const target = e.target as HTMLElement | null;
  if (target?.closest('.word-actions-toolbar') || target?.closest('.flashcard-card')) {
    return;
  }
  closeActionsMenu();
};

onMounted(() => {
  window.addEventListener('touchstart', onWindowTouchStart, { passive: true });
  window.addEventListener('touchmove', onWindowTouchMove, { passive: false });
  window.addEventListener('touchend', onWindowTouchEnd, { passive: true });
  window.addEventListener('touchcancel', onWindowTouchEnd, { passive: true });
  window.addEventListener('pointerdown', onWindowPointerDown, { passive: true });
  window.addEventListener('pointermove', onWindowPointerMove, { passive: true });
  window.addEventListener('pointerup', onWindowPointerUp, { passive: true });
  window.addEventListener('pointercancel', onWindowPointerUp, { passive: true });
  window.addEventListener('click', onWindowClick);
});

onBeforeUnmount(() => {
  window.removeEventListener('touchstart', onWindowTouchStart);
  window.removeEventListener('touchmove', onWindowTouchMove);
  window.removeEventListener('touchend', onWindowTouchEnd);
  window.removeEventListener('touchcancel', onWindowTouchEnd);
  window.removeEventListener('pointerdown', onWindowPointerDown);
  window.removeEventListener('pointermove', onWindowPointerMove);
  window.removeEventListener('pointerup', onWindowPointerUp);
  window.removeEventListener('pointercancel', onWindowPointerUp);
  window.removeEventListener('click', onWindowClick);
  if (closeAnimTimer) clearTimeout(closeAnimTimer);
  if (pulseTimer) clearTimeout(pulseTimer);
});

const handleCardClick = (event: MouseEvent) => {
  // Ignore synthetic click immediately following a swipe gesture
  if (Date.now() - lastSwipeEndAt < 250) {
    return;
  }

  // Prevent action if user selected text
  const selection = window.getSelection();
  if (selection && selection.toString().trim().length > 0) {
    return;
  }

  if (!props.card || props.card.isInformational) {
    return;
  }

  // Prevent action if user clicked interactive buttons (except the spoiler reveal button), inputs, links
  const target = event.target as HTMLElement | null;
  if (
    target?.closest(
      'button:not(.reveal-button), input, select, textarea, a, .word-actions-toolbar, [role="button"]:not(.reveal-button)'
    )
  ) {
    return;
  }

  // Result Card: when typing is submitted or card is a translation result, tapping anywhere advances to next word!
  if (isTypingSubmitted.value || props.card.isTranslationResult) {
    emit('next-word');
    return;
  }

  // Writing drill: do not skip on card tap before typing is submitted
  if ((props.card.isTypingDrill || props.card.level === 5) && !isTypingSubmitted.value) {
    return;
  }

  // 1st tap: if card has a hidden spoiler (Level 3 translation spoiler or Level 4 reverse target word spoiler), reveal it
  const hasHiddenSpoiler =
    Boolean(props.card.isSpoiler || props.card.isReverse || props.card.level === 4) &&
    !isRevealed.value;
  if (hasHiddenSpoiler) {
    isRevealed.value = true;
    return;
  }

  // 2nd tap (or 1st tap when translation is already visible): advance to the next word
  emit('next-word');
};

// Writing drill state
const userTranslationInput = ref('');
watch(userTranslationInput, text => { if (props.card && !props.card.isTranslationResult) props.card.draftInput = text; });
const submittedInput = ref('');
const isTypingSubmitted = ref(false);
const isTypingCorrect = ref(false);

const isResultCorrect = computed(() => {
  if (isTypingSubmitted.value) return isTypingCorrect.value;
  if (props.card?.isTranslationResult) return Boolean(props.card.isCorrect);
  return false;
});

const resultWord = computed(() => {
  let w = props.card?.word || '';
  if (w.includes('Translation is Correct') || w.includes('Wrong translation')) {
    return '';
  }
  return w;
});

const resultTranscription = computed(() => {
  return props.card?.transcription || '';
});

const resultTranslation = computed(() => {
  let tr = props.card?.translation || '';
  if (tr.includes(':') && resultWord.value && tr.includes(resultWord.value)) {
    tr = tr.split(':')[0].trim();
  }
  return tr;
});

watch(
  () => [props.card, props.card?.wordId, props.card?.level, Boolean(props.card?.isTranslationResult)] as const,
  ([newCard, newWordId, newLevel, isResult], [oldCard, oldWordId, oldLevel, wasResult]) => {
    const changedWord = (!isResult && newCard !== oldCard) || newWordId !== oldWordId;
    const changedExercise = !isResult && (newLevel !== oldLevel || wasResult);
    if (changedWord || changedExercise) {
      isRevealed.value = false;
      showActions.value = false;
      menuProgress.value = 0;
      isDraggingMenu.value = false;
      isClosingAnimation.value = false;
      gestureCommitted.value = false;
      halfwayClicked.value = false;
      userTranslationInput.value = '';
      submittedInput.value = '';
      isTypingSubmitted.value = false;
      isTypingCorrect.value = false;
    }
  }
);

const submitTyping = () => {
  const input = userTranslationInput.value.trim();
  if (!input) { emit('next-word'); return; }

  submittedInput.value = input;
  if (props.card) { props.card.submittedInput = input; }
  const expectedWord = (props.card?.word || '').trim();
  const expectedTranslation = (props.card?.translation || '').trim();

  // Match using client-side JS normalization & variant matching
  isTypingCorrect.value =
    checkTranslationMatch(input, expectedWord) ||
    checkTranslationMatch(input, expectedTranslation) ||
    Boolean(expectedWord && input.toLowerCase().includes(expectedWord.toLowerCase())) ||
    Boolean(props.card?.rawText && props.card.rawText.toLowerCase().includes(input.toLowerCase()));

  isTypingSubmitted.value = true;
  emit('submit-reply', input);
};

const handleReaction = async (action: string) => {
  const card = props.card;
  if (!card) return;

  const wordId = card.wordId || 1;
  const updatedWord = await learningEngine.reactToWord(wordId, action, card);

  // If online, also notify legacy endpoint for mock compatibility
  if (card.wordId && localSync.state.connection !== 'offline' && navigator.onLine) {
    void api
      .reactToWord({
        word_id: card.wordId,
        action,
        dictionary_id: card.dictionaryId,
      })
      .catch(() => null);
  }

  // Persistence can finish after Next changes the prop. Keep the result and
  // notification attached to the card that received the action.
  if (updatedWord) {
    card.level = updatedWord.stars;
  }

  emit('word-action', action, card);
};


</script>

<style scoped>
.flashcard-card,
.welcome-card {
  background: var(--ui-card-surface);
  border-color: var(--ui-card-border);
  box-shadow: var(--ui-shadow-elevated);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--ui-text);
}
.word-text {
  color: var(--ui-text);
}
.word-actions-toolbar {
  will-change: opacity, transform, max-height;
}
.word-actions-toolbar--pulse {
  box-shadow: 0 0 0 2px var(--ui-primary);
}
.typing-feedback--success {
  background-color: var(--ui-success-surface);
  border-color: var(--ui-success-border);
  color: var(--ui-success-text);
}
.typing-feedback--danger {
  background-color: var(--ui-danger-surface);
  border-color: var(--ui-danger-border);
  color: var(--ui-danger-text);
}
</style>
